import "../../types.js";
import { caseCommentsInsertSchema, caseCommentsRowSchema, caseFilesInsertSchema, caseFilesRowSchema, caseFilesUpdateSchema, caseTagsInsertSchema, caseTagsRowSchema, caseTagsUpdateSchema, caseTemplatesInsertSchema, caseTemplatesRowSchema, caseTemplatesToPublicPortalsRowSchema, caseTemplatesUpdateSchema, casesInsertSchema, casesRowSchema, casesToCaseTagsRowSchema, casesToDeviceDataTriggersRowSchema, casesToMaintenancesRowSchema, casesToPartsRowSchema, casesToServiceJobsRowSchema, casesUpdateSchema, checklistTemplateItemsRowSchema, checklistTemplatesRowSchema, deploymentSettingsRowSchema, deploymentSettingsUpdateSchema, deploymentsRowSchema, deviceDataSeriesRowSchema, deviceDataTriggersInsertSchema, deviceDataTriggersRowSchema, deviceDataTriggersUpdateSchema, deviceModelDataTriggersInsertSchema, deviceModelDataTriggersRowSchema, deviceModelsRowSchema, deviceTagsRowSchema, devicesInsertSchema, devicesRowSchema, devicesUpdateSchema, maintenanceApprovalFilesInsertSchema, maintenanceApprovalFilesRowSchema, maintenanceApprovalFilesUpdateSchema, maintenanceApprovalsInsertSchema, maintenanceApprovalsRowSchema, maintenanceApprovalsUpdateSchema, maintenanceItemFilesInsertSchema, maintenanceItemFilesRowSchema, maintenanceItemFilesUpdateSchema, maintenanceItemTemplateFilesRowSchema, maintenanceItemsInsertSchema, maintenanceItemsRowSchema, maintenanceItemsUpdateSchema, maintenanceStrategiesHistoryRowSchema, maintenanceStrategiesInsertSchema, maintenanceStrategiesRowSchema, maintenanceStrategiesToDevicesRowSchema, maintenanceStrategiesToMaintenanceTagsRowSchema, maintenanceStrategiesUpdateSchema, maintenanceTagsInsertSchema, maintenanceTagsRowSchema, maintenanceTagsUpdateSchema, maintenancesInsertSchema, maintenancesRowSchema, maintenancesToDeviceDataTriggersRowSchema, maintenancesToDevicesRowSchema, maintenancesToMaintenanceTagsRowSchema, maintenancesToMaintenancesRowSchema, maintenancesToServiceJobsRowSchema, maintenancesUpdateSchema, manufacturersRowSchema, publicCaseStatusSchema, publicCaseTypeSchema, publicCasesHistoryTypeSchema, publicCasesInsightsPeriodSchema, publicDataTriggerTargetTableSchema, publicDataTriggerTransformationSchema, publicDbOperationSchema, publicMaintenanceItemStatusSchema, publicMaintenanceStatusSchema, publicMaintenanceStrategiesHistoryTypeSchema, publicMaintenancesFinishedDeviceUtilizationPeriodSchema, publicMaintenancesHistoryTypeSchema, publicPortalsInsertSchema, publicPortalsRowSchema, publicPortalsUpdateSchema, publicWorkspaceRoleSchema, qrCodesInsertSchema, qrCodesRowSchema, qrCodesUpdateSchema, userProfilesRowSchema, workspaceInvitationsRowSchema } from "../../schemas.js";
import { paginationParamModel } from "../../models/paginationParamModel.js";
import { stringToArrayModel } from "../../models/stringToArrayModel.js";
import { globalFilterParamModel } from "../../models/globalFilterParamModel.js";
import { idParamModel } from "../../models/idParamModel.js";
import { searchParamModel } from "../../models/searchParamModel.js";
import { getSortParamModel } from "../../models/sortParamModel.js";
import * as z from "zod";
//#region src/contracts/internal/auth.ts
const registerUserBodySchema = z.object({
	email: z.string().trim().toLowerCase().pipe(z.email()),
	password: z.string().min(1),
	language: z.string().optional(),
	whitelabelBrandKey: z.string().optional(),
	appBaseUrl: z.string().optional()
});
const resetPasswordBodySchema = z.object({
	email: z.string().trim().toLowerCase().pipe(z.email()),
	language: z.string().optional(),
	whitelabelBrandKey: z.string().optional(),
	appBaseUrl: z.string().optional()
});
//#endregion
//#region src/contracts/internal/caseFiles.ts
const caseFilesTable = "case_files";
const BUCKET_CASE_FILES = "case-files";
const caseFileSchema = caseFilesRowSchema;
const caseFileListQueryParamsSchema = z.object({
	case_id: z.string().optional(),
	case_comment_id: z.string().optional()
}).refine(({ case_id, case_comment_id }) => !case_id !== !case_comment_id, { message: "Exactly one of case_id or case_comment_id is required" });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), bucket_id (always BUCKET_CASE_FILES)
* Insert Required: case_id, path (of an already uploaded storage object, below the case's workspace folder)
* Insert Optional: case_comment_id (a comment of the same case)
*/
const caseFileInsertSchema = caseFilesInsertSchema.omit({
	id: true,
	user_id: true,
	bucket_id: true,
	created_at: true,
	updated_at: true
});
/**
* Update Allowed: file meta information only (title, description, thumbnail, locale)
*/
const caseFileUpdateSchema = caseFilesUpdateSchema.pick({
	title: true,
	description: true,
	thumbnail: true,
	locale: true
});
//#endregion
//#region src/contracts/internal/deviceDataTriggers.ts
const deviceDataTriggersTable = "device_data_triggers";
const deviceDataTriggerSchema = deviceDataTriggersRowSchema;
const deviceDataTriggerWithSeriesSchema = z.strictObject({
	...deviceDataTriggersRowSchema.shape,
	series: deviceDataSeriesRowSchema.nullable()
});
const deviceDataTriggerCurrentValueSchema = z.object({
	value: z.number().nullable(),
	last_data_at: z.string().nullable()
});
const deviceDataTriggerEvaluationSchema = z.object({ fired: z.boolean() });
const deviceDataSeriesConfigSchema = deviceDataSeriesRowSchema.pick({
	target_table: true,
	metric_name: true,
	calculate_derivative: true,
	transform: true,
	time_window: true
});
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), the series (chosen with an update), the model
* trigger (set by the model trigger) and the evaluation state
*/
const deviceDataTriggerInsertSchema = deviceDataTriggersInsertSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true,
	series_id: true,
	device_model_data_trigger_id: true,
	last_run_at: true,
	last_data_at: true,
	last_value: true,
	next_fire_value: true,
	firing_since: true
});
/**
* Update Disallowed: id, creator user, timestamps (auto-generated), deployment (moves with its device), the model
* trigger and the evaluation state - except for the last value, which is re-based with a new offset
* Update Allowed: the series, by its calculation
*/
const deviceDataTriggerUpdateSchema = deviceDataTriggersUpdateSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true,
	deployment_id: true,
	series_id: true,
	device_model_data_trigger_id: true,
	last_run_at: true,
	last_data_at: true,
	next_fire_value: true,
	firing_since: true
}).extend({ series: deviceDataSeriesConfigSchema.optional() });
//#endregion
//#region src/contracts/internal/maintenanceStrategies.ts
const maintenanceStrategiesTable = "maintenance_strategies";
const maintenanceStrategiesToDevicesTable = "maintenance_strategies_to_devices";
const maintenanceStrategiesHistoryTable = "maintenance_strategies_history";
const maintenanceStrategiesToMaintenanceTagsTable = "maintenance_strategies_to_maintenance_tags";
const MAINTENANCE_STRATEGY_NO_SCHEDULE_INTERVAL = "";
const maintenanceStrategySearchables = [{
	table: maintenanceStrategiesTable,
	column: "title"
}, {
	table: maintenanceStrategiesTable,
	column: "description"
}];
const maintenanceStrategySortables = [
	{
		table: maintenanceStrategiesTable,
		column: "title"
	},
	{
		table: maintenanceStrategiesTable,
		column: "created_at"
	},
	{
		table: maintenanceStrategiesTable,
		column: "updated_at"
	},
	{
		table: maintenanceStrategiesTable,
		column: "is_active"
	}
];
const maintenanceStrategyLogSortables = [{
	table: maintenanceStrategiesHistoryTable,
	column: "created_at"
}];
const maintenanceStrategySchema = maintenanceStrategiesRowSchema;
const maintenanceStrategyExtendedSchema = z.strictObject({
	...maintenanceStrategiesRowSchema.shape,
	maintenance_strategies_to_devices: z.array(z.object({
		...maintenanceStrategiesToDevicesRowSchema.pick({ device_id: true }).shape,
		device: z.object({
			...devicesRowSchema.pick({
				id: true,
				model_id: true
			}).shape,
			device_tags: z.array(z.object({ id: z.string() }))
		}).nullable()
	})),
	maintenance_tags: z.array(maintenanceTagsRowSchema)
});
const maintenanceStrategyDeviceSchema = z.strictObject({
	...maintenanceStrategiesToDevicesRowSchema.shape,
	device: z.object({
		...devicesRowSchema.shape,
		device_tags: z.array(deviceTagsRowSchema),
		manufacturer: manufacturersRowSchema.nullable(),
		model: deviceModelsRowSchema.nullable()
	}).nullable(),
	checklist_template: checklistTemplatesRowSchema.nullable()
});
const maintenanceStrategyMaintenanceTagSchema = maintenanceStrategiesToMaintenanceTagsRowSchema;
const maintenanceStrategyActiveSchema = z.strictObject({
	...maintenanceStrategiesRowSchema.shape,
	maintenance_strategies_to_devices: z.array(maintenanceStrategiesToDevicesRowSchema.pick({ device_id: true }))
});
const maintenanceStrategySummarySchema = z.object({
	...maintenanceStrategiesRowSchema.pick({
		title: true,
		is_active: true
	}).shape,
	maintenance_strategies_to_devices: z.array(maintenanceStrategiesToDevicesRowSchema.pick({ device_id: true }))
});
const maintenanceStrategyLogValSchema = z.looseObject(maintenanceStrategiesRowSchema.partial().shape).nullable();
const maintenanceStrategyLogSchema = z.strictObject({
	...maintenanceStrategiesHistoryRowSchema.shape,
	type: publicMaintenanceStrategiesHistoryTypeSchema,
	new_val: maintenanceStrategyLogValSchema,
	old_val: maintenanceStrategyLogValSchema
});
const maintenanceStrategyListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceStrategySortables,
		default_sort_by: "maintenance_strategies.title",
		default_sort_dir: "asc"
	}).shape,
	is_active: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean().optional()),
	...globalFilterParamModel.shape
});
const maintenanceStrategyDevicesDeleteQueryParamsSchema = z.object({ device_ids: stringToArrayModel(z.string().array().nonempty()) });
const maintenanceStrategyMaintenanceTagParamsSchema = idParamModel.extend({ maintenance_tag_id: idParamModel.shape.id });
const maintenanceStrategyLogListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceStrategyLogSortables,
		default_sort_by: "maintenance_strategies_history.created_at",
		default_sort_dir: "desc"
	}).shape
});
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), workspace_id (derived from the deployment),
* is_active (always active on create), excluded_dates
* Insert Optional: device_ids — linked in the same transaction (devices must belong to the deployment)
*/
const maintenanceStrategyInsertSchema = z.strictObject({
	...maintenanceStrategiesInsertSchema.pick({
		title: true,
		description: true,
		deployment_id: true,
		duration: true,
		assignee_id: true
	}).shape,
	schedule_interval: maintenanceStrategiesInsertSchema.shape.schedule_interval.optional(),
	device_ids: z.array(z.string()).optional()
});
/** Insert Required: device_id; Insert Optional: checklist_template_id — ordering is appended server-side */
const maintenanceStrategyDevicesInsertSchema = z.strictObject({ devices: z.array(z.strictObject({
	device_id: z.string(),
	checklist_template_id: z.string().nullable().optional()
})).nonempty() });
/** Appends one date to excluded_dates (deduplicated by UTC day) */
const maintenanceStrategyExcludedDateInsertSchema = z.strictObject({ date: z.iso.datetime({ offset: true }) });
/**
* Update Allowed: title, description, duration, assignee_id, schedule_interval,
* deployment_id (only while no devices are linked; workspace_id is re-derived)
* Update Disallowed: is_active (see maintenanceStrategySetActiveSchema), excluded_dates (see maintenanceStrategyExcludedDateInsertSchema)
*/
const maintenanceStrategyUpdateSchema = maintenanceStrategiesUpdateSchema.pick({
	title: true,
	description: true,
	duration: true,
	assignee_id: true,
	schedule_interval: true,
	deployment_id: true
}).strict();
/** Sets the checklist template of the given device links (null removes it) */
const maintenanceStrategyDevicesUpdateSchema = z.strictObject({
	device_ids: z.array(z.string()).nonempty(),
	checklist_template_id: z.string().nullable()
});
/**
* Moves one device link onto the position of another device link of the strategy (the drop target) — the
* absolute position is resolved server-side, so it stays correct while the client list is searched / paged
*/
const maintenanceStrategyDeviceReorderSchema = z.strictObject({
	device_id: z.string(),
	target_device_id: z.string()
});
/** Update Allowed: is_active (pause / resume) */
const maintenanceStrategySetActiveSchema = z.strictObject({ is_active: maintenanceStrategiesUpdateSchema.shape.is_active.unwrap() });
//#endregion
//#region src/contracts/internal/maintenances.ts
const maintenancesTable = "maintenances";
const maintenancesToDevicesTable = "maintenances_to_devices";
const maintenancesToMaintenanceTagsTable = "maintenances_to_maintenance_tags";
const maintenanceItemsTable = "maintenance_items";
const maintenanceItemsToPartsTable = "maintenance_items_to_parts";
const maintenancesToMaintenancesTable = "maintenances_to_maintenances";
const maintenancesToServiceJobsTable = "maintenances_to_service_jobs";
const maintenancesToDeviceDataTriggersTable = "maintenances_to_device_data_triggers";
const maintenancesHistoryTable = "maintenances_history";
const maintenancesToDevicesHistoryTable = "maintenances_to_devices_history";
const maintenanceApprovalsHistoryTable = "maintenance_approvals_history";
const maintenancesToMaintenanceTagsHistoryTable = "maintenances_to_maintenance_tags_history";
const maintenancesToMaintenancesHistoryTable = "maintenances_to_maintenances_history";
const maintenancesToServiceJobsHistoryTable = "maintenances_to_service_jobs_history";
const MAINTENANCE_STATUS_TRANSITIONS = {
	["scheduled"]: ["started", "skipped"],
	["started"]: ["finished", "skipped"],
	["skipped"]: [],
	["finished"]: []
};
const STAGING_PART_LOCATION_TEMPLATE_NAME = "Staging";
const maintenanceSearchables = [
	{
		table: maintenancesTable,
		column: "title"
	},
	{
		table: maintenancesTable,
		column: "notes"
	},
	{
		table: maintenancesTable,
		column: "skip_reason"
	},
	{
		table: maintenancesTable,
		column: "public_id"
	},
	{
		table: "maintenance_tags",
		column: "name"
	},
	{
		table: "maintenance_strategies",
		column: "title"
	},
	{
		table: "maintenance_items",
		column: "notes"
	}
];
const maintenanceSortables = [
	{
		table: maintenancesTable,
		column: "title"
	},
	{
		table: maintenancesTable,
		column: "status"
	},
	{
		table: maintenancesTable,
		column: "updated_at"
	},
	{
		table: maintenancesTable,
		column: "created_at"
	},
	{
		table: maintenancesTable,
		column: "resolved_at"
	},
	{
		table: maintenancesTable,
		column: "scheduled_at"
	},
	{
		table: maintenancesTable,
		column: "planned_at"
	},
	{
		table: maintenancesTable,
		column: "planned_date"
	},
	{
		table: maintenancesTable,
		column: "planned_date_until"
	},
	{
		table: maintenancesTable,
		column: "public_id"
	}
];
const maintenanceSchema = maintenancesRowSchema;
const maintenanceExtendedSchema = z.strictObject({
	...maintenancesRowSchema.shape,
	child_maintenances: z.array(maintenancesToMaintenancesRowSchema.pick({ maintenance_child_id: true })),
	parent_maintenances: z.array(maintenancesToMaintenancesRowSchema.pick({ maintenance_parent_id: true })),
	cases_to_maintenances: z.array(casesToMaintenancesRowSchema.pick({ case_id: true })),
	service_jobs: z.array(maintenancesToServiceJobsRowSchema.pick({ service_job_id: true })),
	maintenances_to_device_data_triggers: z.array(maintenancesToDeviceDataTriggersRowSchema.pick({ device_data_trigger_id: true })),
	assignee: userProfilesRowSchema.nullable(),
	maintenances_to_devices: z.array(z.object({
		...maintenancesToDevicesRowSchema.pick({ device_id: true }).shape,
		device: z.object({
			...devicesRowSchema.pick({
				id: true,
				model_id: true
			}).shape,
			device_tags: z.array(z.object({ id: z.string() }))
		}).nullable()
	})),
	maintenance_tags: z.array(maintenanceTagsRowSchema),
	maintenance_strategy: maintenanceStrategySummarySchema.nullable(),
	planned_devices_count: z.number(),
	status_progress: z.number(),
	status_progress_max: z.number(),
	status_progress_details: z.object({
		pending: z.number(),
		started: z.number(),
		skipped: z.number(),
		failed: z.number(),
		completed: z.number()
	})
});
const maintenanceViewSchema = z.enum([
	"open",
	"history",
	"upcoming",
	"calendar"
]);
const maintenanceCustomFilterSchema = z.enum([
	"unassigned",
	"due",
	"today",
	"notes",
	"partsUsed"
]);
const VIRTUAL_MAINTENANCE_PREFIX = "VIRTUAL";
const MAINTENANCE_SKIP_REASON_AUTO_SKIP = "AUTO_SKIP_BY_NEW_MAINTENANCE";
const MAINTENANCE_ASSIGNABLE_DEPLOYMENT_ROLES = ["admin", "technician"];
const maintenanceListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceSortables,
		default_sort_by: "maintenances.title",
		default_sort_dir: "asc"
	}).shape,
	statuses: stringToArrayModel(publicMaintenanceStatusSchema.array().nonempty().optional()),
	maintenanceStrategyId: z.string().optional().nullable(),
	deploymentId: z.string().optional(),
	assigneeId: z.string().optional(),
	...globalFilterParamModel.shape,
	maintenanceTagIds: stringToArrayModel(z.string().array().nonempty().optional()),
	deviceDataTriggerId: z.string().optional(),
	customFilter: maintenanceCustomFilterSchema.optional(),
	view: maintenanceViewSchema.optional(),
	plannedDate__gte: z.iso.datetime({ offset: true }).optional(),
	plannedDate__lte: z.iso.datetime({ offset: true }).optional(),
	resolvedDate__gte: z.iso.datetime({ offset: true }).optional(),
	resolvedDate__lte: z.iso.datetime({ offset: true }).optional(),
	ids: stringToArrayModel(z.string().array().nonempty().optional()),
	excludeIds: stringToArrayModel(z.string().array().nonempty().optional()),
	serviceJobId: z.string().optional(),
	plannedWindow__gte: z.iso.datetime({ offset: true }).optional(),
	plannedWindow__lte: z.iso.datetime({ offset: true }).optional(),
	today__gte: z.iso.datetime({ offset: true }).optional(),
	today__lte: z.iso.datetime({ offset: true }).optional()
});
const maintenanceUpcomingVirtualQueryParamsSchema = z.object({
	plannedDate__gte: z.iso.datetime({ offset: true }),
	plannedDate__lte: z.iso.datetime({ offset: true }),
	maintenanceStrategyId: z.string().optional(),
	deploymentId: z.string().optional(),
	assigneeId: z.string().optional(),
	...globalFilterParamModel.shape,
	maintenanceTagIds: stringToArrayModel(z.string().array().nonempty().optional()),
	statuses: stringToArrayModel(publicMaintenanceStatusSchema.array().nonempty().optional())
});
/**
* Materializes a maintenance_strategy occurrence (eg. a virtual maintenance) into a real maintenance,
* see packages/backend/src/modules/shared/maintenances.ts:createMaintenanceFromStrategy
* Optional: scheduledAt (default: now), title (default: strategy title), assigneeId (default: strategy assignee,
* then deployment's default maintenance assignee), serviceJobId (links the maintenance to a service job)
*/
const maintenanceFromStrategyCreateSchema = z.object({
	maintenanceStrategyId: z.string(),
	scheduledAt: z.iso.datetime({ offset: true }).optional(),
	title: z.string().optional(),
	assigneeId: z.uuid().optional(),
	serviceJobId: z.string().optional()
});
/**
* Creates a maintenance (formerly RPC `public.create_maintenance`), with its devices, links and tags in one
* transaction, see packages/backend/src/modules/shared/maintenances.ts:createMaintenance
* Insert Disallowed: workspace_id (derived from the deployment), status (always scheduled), creator user, strategy
* Insert Optional: scheduled_at (default: now), scheduled_duration (default: the checklist template's content duration
* per device), assignee_id (default: deployment's default maintenance assignee), service_job_id, case_id (links the
* maintenance as child of the case), maintenance_id / maintenance_item_id (links it as child of the maintenance; the
* item takes precedence), maintenance_tag_ids, device_ids, checklist_template_id (for all devices)
*/
const maintenanceInsertSchema = z.strictObject({
	...maintenancesInsertSchema.pick({
		title: true,
		deployment_id: true,
		scheduled_at: true,
		scheduled_duration: true,
		assignee_id: true
	}).shape,
	service_job_id: z.string().optional(),
	case_id: z.string().optional(),
	maintenance_id: z.string().optional(),
	maintenance_item_id: z.string().optional(),
	maintenance_tag_ids: z.array(z.string()).optional(),
	device_ids: z.array(z.string()).optional(),
	checklist_template_id: z.string().optional()
});
/**
* Update Allowed: title, notes, assignee_id (must be admin / technician of the deployment), planning and status dates
* Update Disallowed: status (start / skip / finish), deployment / workspace, strategy, serial ids, durations derived
* by triggers
*/
const maintenanceUpdateSchema = maintenancesUpdateSchema.pick({
	title: true,
	notes: true,
	assignee_id: true,
	planned_at: true,
	planned_duration: true,
	started_at: true,
	resolved_at: true
}).strict();
const maintenanceSkipSchema = z.strictObject({
	skip_reason: z.string(),
	assign_to_me: z.boolean().optional()
});
const maintenanceFinishSchema = z.strictObject({
	skip_remaining_items: z.boolean().optional(),
	assign_to_me: z.boolean().optional()
});
const maintenanceLogSchema = z.strictObject({
	type: publicMaintenancesHistoryTypeSchema,
	id: z.string(),
	created_at: z.string().nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	maintenance_id: z.string().nullable(),
	new_val: z.record(z.string(), z.unknown()).nullable(),
	old_val: z.record(z.string(), z.unknown()).nullable(),
	changed_fields: z.array(z.string()).nullable()
});
const maintenanceLogListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...getSortParamModel({
		sortables: [{
			table: maintenancesHistoryTable,
			column: "created_at"
		}],
		default_sort_by: "maintenances_history.created_at",
		default_sort_dir: "desc"
	}).shape
});
const maintenanceFollowupDurationSchema = z.strictObject({
	planned_duration_ms: z.number(),
	actual_duration_ms: z.number()
});
const maintenanceByIdsQueryParamsSchema = z.object({ ids: stringToArrayModel(z.string().array().nonempty()) });
const maintenanceFilterParamModel = z.object({
	...globalFilterParamModel.shape,
	maintenanceTagIds: stringToArrayModel(z.string().array().nonempty().optional())
});
const maintenanceExportQueryParamsSchema = z.object({
	...maintenanceFilterParamModel.shape,
	statuses: stringToArrayModel(publicMaintenanceStatusSchema.array().nonempty()),
	from: z.iso.datetime({ offset: true }),
	until: z.iso.datetime({ offset: true })
});
const MAINTENANCE_EXPORT_CSV_COLUMNS = [
	"planned_at",
	"started_at",
	"resolved_at",
	"workspace",
	"deployment",
	"workorder",
	"strategy",
	"status",
	"tags",
	"devices",
	"notes",
	"assignee",
	"created_by"
];
const maintenanceItemExportQueryParamsSchema = z.object({
	...maintenanceFilterParamModel.shape,
	statuses: stringToArrayModel(publicMaintenanceItemStatusSchema.array().nonempty()),
	from: z.iso.datetime({ offset: true }),
	until: z.iso.datetime({ offset: true }),
	includeDeviceLogs: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean().optional())
});
const MAINTENANCE_ITEM_EXPORT_CSV_COLUMNS = [
	"created_at",
	"started_at",
	"resolved_at",
	"workspace",
	"deployment",
	"workorder",
	"workorder_tags",
	"strategy",
	"task",
	"description",
	"status",
	"notes",
	"device",
	"device_ref",
	"assignee",
	"created_by",
	"checklist_template"
];
const maintenanceInsightsQueryParamsSchema = z.object({
	...maintenanceFilterParamModel.shape,
	from: z.iso.datetime({ offset: true }),
	until: z.iso.datetime({ offset: true })
});
const maintenanceInsightsPeriodQueryParamsSchema = maintenanceInsightsQueryParamsSchema.extend({ period: publicMaintenancesFinishedDeviceUtilizationPeriodSchema });
const maintenanceInsightsAvgResolutionQueryParamsSchema = maintenanceFilterParamModel;
const maintenanceActivityQueryParamsSchema = z.object({
	...maintenanceFilterParamModel.shape,
	since: z.iso.datetime({ offset: true })
});
const maintenanceInsightsFinishedCountSchema = z.strictObject({
	workspace_id: z.string(),
	workspace_name: z.string(),
	deployment_id: z.string(),
	deployment_name: z.string(),
	device_id: z.string(),
	device_name: z.string(),
	assignee_id: z.string().nullable(),
	assignee_name: z.string().nullable(),
	maintenance_tag_id: z.string().nullable(),
	maintenance_tag_name: z.string().nullable(),
	maintenance_count: z.number(),
	maintenance_ids: z.array(z.string())
});
const maintenanceInsightsResolveDurationsSchema = z.strictObject({
	min_duration_ms: z.number().nullable(),
	max_duration_ms: z.number().nullable(),
	avg_duration_ms: z.number().nullable(),
	total_duration_ms: z.number().nullable(),
	min_items_duration_ms: z.number().nullable(),
	max_items_duration_ms: z.number().nullable(),
	avg_items_duration_ms: z.number().nullable(),
	total_items_duration_ms: z.number().nullable()
});
const maintenanceInsightsResolveTimeSchema = maintenanceInsightsResolveDurationsSchema.extend({
	maintenance_tag_id: z.string().nullable(),
	maintenance_tag_name: z.string().nullable(),
	device_count: z.number(),
	maintenance_count: z.number(),
	total_unique_device_count: z.number()
});
const maintenanceInsightsResolveTimeByDeviceSchema = maintenanceInsightsResolveDurationsSchema.extend({
	device_id: z.string(),
	device_name: z.string(),
	device_count: z.number(),
	maintenance_count: z.number()
});
const maintenanceInsightsDeviceUtilizationSchema = z.strictObject({
	period_start: z.string(),
	period_end: z.string(),
	total_devices: z.number(),
	devices_count: z.number(),
	percentage: z.number().nullable(),
	device_ids: z.array(z.string())
});
const maintenanceInsightsByStatusTagPeriodSchema = z.strictObject({
	period_start: z.string(),
	period_end: z.string(),
	maintenance_tag_id: z.string().nullable(),
	maintenance_tag_name: z.string().nullable(),
	status: publicMaintenanceStatusSchema.nullable(),
	maintenance_count: z.number(),
	maintenance_ids: z.array(z.string())
});
const maintenanceInsightsAvgResolutionSchema = z.strictObject({ avg_resolution_duration_ms: z.number().nullable() });
const maintenanceActivitySchema = maintenancesRowSchema.pick({
	id: true,
	status: true,
	planned_date: true,
	started_at: true,
	resolved_at: true
});
const maintenanceDeviceSchema = z.strictObject({
	...maintenancesToDevicesRowSchema.shape,
	device: z.object({
		...devicesRowSchema.shape,
		device_tags: z.array(deviceTagsRowSchema),
		manufacturer: manufacturersRowSchema.nullable(),
		model: deviceModelsRowSchema.nullable()
	}).nullable(),
	checklist_template: z.object({
		...checklistTemplatesRowSchema.shape,
		checklist_template_items: z.array(checklistTemplateItemsRowSchema)
	}).nullable()
});
const maintenanceDeviceParamsSchema = idParamModel.extend({ device_id: idParamModel.shape.id });
/**
* Adds devices to a scheduled / started maintenance (devices of its deployment) — ordering is appended server-side.
* On a started maintenance the items of the devices' checklist templates are created right away
*/
const maintenanceDevicesInsertSchema = z.strictObject({ devices: z.array(z.strictObject({
	device_id: z.string(),
	checklist_template_id: z.string().nullable().optional()
})).nonempty() });
/** Sets the checklist template of a device link (null removes it) */
const maintenanceDeviceUpdateSchema = z.strictObject({ checklist_template_id: z.string().nullable() });
/** Removes devices (with their pending items) — refused if one of them has an item that is not pending anymore */
const maintenanceDevicesDeleteQueryParamsSchema = z.object({ device_ids: stringToArrayModel(z.string().array().nonempty()) });
/**
* Moves one device link onto the position of another device link of the maintenance (the drop target) — the
* absolute position is resolved server-side, so it stays correct while the client list is filtered / searched / paged
*/
const maintenanceDeviceReorderSchema = z.strictObject({
	device_id: z.string(),
	target_device_id: z.string()
});
const maintenanceLinkedMaintenanceSchema = z.strictObject({
	...maintenancesToMaintenancesRowSchema.pick({
		maintenance_parent_id: true,
		maintenance_child_id: true,
		maintenance_item_id: true,
		created_at: true,
		user_id: true
	}).shape,
	maintenance: maintenanceExtendedSchema
});
const maintenanceMaintenanceLinkSchema = maintenancesToMaintenancesRowSchema;
const maintenanceChildParamsSchema = idParamModel.extend({ child_id: idParamModel.shape.id });
const maintenanceParentParamsSchema = idParamModel.extend({ parent_id: idParamModel.shape.id });
const maintenanceMaintenanceTagLinkSchema = maintenancesToMaintenanceTagsRowSchema;
const maintenanceMaintenanceTagParamsSchema = idParamModel.extend({ maintenance_tag_id: idParamModel.shape.id });
const maintenanceMaintainedDevicesCountQueryParamsSchema = z.object({ since: z.iso.datetime({ offset: true }) });
const maintenanceCountSchema = z.strictObject({ count: z.number() });
//#endregion
//#region src/contracts/internal/cases.ts
const casesTable = "cases";
const casesHistoryTable = "cases_history";
const caseCommentsTable = "case_comments";
const caseTagsTable = "case_tags";
const casesToCaseTagsTable = "cases_to_case_tags";
const casesToCaseTagsHistoryTable = "cases_to_case_tags_history";
const casesToMaintenancesTable = "cases_to_maintenances";
const casesToMaintenancesHistoryTable = "cases_to_maintenances_history";
const casesToPartsTable = "cases_to_parts";
const casesToPartsHistoryTable = "cases_to_parts_history";
const casesToServiceJobsTable = "cases_to_service_jobs";
const casesToServiceJobsHistoryTable = "cases_to_service_jobs_history";
const casesToDeviceDataTriggersTable = "cases_to_device_data_triggers";
const CASE_EDITABLE_STATUSES = [
	"reported",
	"assessed",
	"started"
];
const CASE_CLOSED_STATUSES = ["resolved", "archived"];
const caseSearchables = [
	{
		table: casesTable,
		column: "name"
	},
	{
		table: casesTable,
		column: "public_id"
	},
	{
		table: "devices",
		column: "name"
	},
	{
		table: "maintenances",
		column: "title"
	},
	{
		table: caseTagsTable,
		column: "name"
	}
];
const caseSortables = [
	{
		table: casesTable,
		column: "name"
	},
	{
		table: casesTable,
		column: "updated_at"
	},
	{
		table: casesTable,
		column: "created_at"
	},
	{
		table: casesTable,
		column: "due_at"
	},
	{
		table: casesTable,
		column: "reported_at"
	},
	{
		table: casesTable,
		column: "assessed_at"
	},
	{
		table: casesTable,
		column: "started_at"
	},
	{
		table: casesTable,
		column: "resolved_at"
	},
	{
		table: casesTable,
		column: "public_id"
	},
	{
		table: casesTable,
		column: "origin"
	},
	{
		table: casesTable,
		column: "priority"
	},
	{
		table: casesTable,
		column: "type"
	},
	{
		table: casesTable,
		column: "status"
	}
];
const caseSchema = casesRowSchema.omit({
	embedding: true,
	fts: true
});
const caseExtendedSchema = z.strictObject({
	...caseSchema.shape,
	device: z.object({
		...devicesRowSchema.shape,
		device_tags: z.array(deviceTagsRowSchema),
		manufacturer: manufacturersRowSchema.nullable(),
		model: deviceModelsRowSchema.nullable()
	}).nullable(),
	parts: z.array(casesToPartsRowSchema.pick({ id: true })),
	cases_to_maintenances: z.array(casesToMaintenancesRowSchema),
	cases_to_device_data_triggers: z.array(casesToDeviceDataTriggersRowSchema.pick({ device_data_trigger_id: true })),
	cases_to_service_jobs: z.array(casesToServiceJobsRowSchema.pick({ service_job_id: true })),
	case_tags: z.array(caseTagsRowSchema)
});
const caseLogSchema = z.strictObject({
	type: publicCasesHistoryTypeSchema,
	id: z.string(),
	created_at: z.string().nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	case_id: z.string().nullable(),
	new_val: z.record(z.string(), z.unknown()).nullable(),
	old_val: z.record(z.string(), z.unknown()).nullable(),
	changed_fields: z.array(z.string()).nullable()
});
const caseCommentSchema = caseCommentsRowSchema;
const caseMaintenanceLinkSchema = casesToMaintenancesRowSchema.pick({
	case_id: true,
	maintenance_id: true,
	origin: true,
	created_at: true,
	user_id: true
});
const caseLinkedMaintenanceSchema = z.strictObject({
	...caseMaintenanceLinkSchema.shape,
	maintenance: maintenanceExtendedSchema
});
const maintenanceLinkedCaseSchema = z.strictObject({
	...caseMaintenanceLinkSchema.shape,
	case: caseExtendedSchema
});
const caseServiceJobLinkSchema = casesToServiceJobsRowSchema;
const caseTagLinkSchema = casesToCaseTagsRowSchema;
const caseDeviceDataTriggerSchema = deviceDataTriggerWithSeriesSchema;
const caseActivitySchema = casesRowSchema.pick({
	id: true,
	status: true,
	reported_at: true,
	assessed_at: true,
	started_at: true,
	resolved_at: true
});
const entityTreeNodeSchema = z.discriminatedUnion("entity_type", [z.strictObject({
	entity_type: z.literal("case"),
	entity_id: z.string(),
	parent_id: z.string().nullable(),
	entity: casesRowSchema.pick({
		id: true,
		name: true
	})
}), z.strictObject({
	entity_type: z.literal("maintenance"),
	entity_id: z.string(),
	parent_id: z.string().nullable(),
	entity: maintenancesRowSchema.pick({
		id: true,
		title: true
	})
})]);
const caseCustomFilterSchema = z.enum(["unassigned", "due"]);
const caseFilterParamModel = z.object({
	...globalFilterParamModel.shape,
	caseTagIds: stringToArrayModel(z.string().array().nonempty().optional()),
	caseTypes: stringToArrayModel(publicCaseTypeSchema.array().nonempty().optional())
});
const caseListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: caseSortables,
		default_sort_by: "cases.created_at",
		default_sort_dir: "desc"
	}).shape,
	...caseFilterParamModel.shape,
	statuses: stringToArrayModel(publicCaseStatusSchema.array().nonempty().optional()),
	ids: stringToArrayModel(z.string().array().nonempty().optional()),
	excludeIds: stringToArrayModel(z.string().array().nonempty().optional()),
	deviceDataTriggerId: z.string().optional(),
	customFilter: caseCustomFilterSchema.optional(),
	dueAt__lt: z.iso.datetime({ offset: true }).optional(),
	closedAt__gte: z.iso.datetime({ offset: true }).optional(),
	hasProposal: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean().optional()),
	hasMaintenance: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean().optional())
});
const caseCommentParamsSchema = idParamModel.extend({ comment_id: idParamModel.shape.id });
const caseCaseTagParamsSchema = idParamModel.extend({ case_tag_id: idParamModel.shape.id });
const caseMaintenanceParamsSchema = idParamModel.extend({ maintenance_id: idParamModel.shape.id });
const maintenanceCaseParamsSchema = idParamModel.extend({ case_id: idParamModel.shape.id });
const caseServiceJobParamsSchema = idParamModel.extend({ service_job_id: idParamModel.shape.id });
const serviceJobCaseParamsSchema = idParamModel.extend({ case_id: idParamModel.shape.id });
const caseActivityQueryParamsSchema = z.object({
	...caseFilterParamModel.shape,
	since: z.iso.datetime({ offset: true })
});
const caseByIdsQueryParamsSchema = z.object({ ids: stringToArrayModel(z.string().array().nonempty()) });
const caseLogListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...getSortParamModel({
		sortables: [{
			table: casesHistoryTable,
			column: "created_at"
		}],
		default_sort_by: "cases_history.created_at",
		default_sort_dir: "desc"
	}).shape
});
const caseExportQueryParamsSchema = z.object({
	...caseFilterParamModel.shape,
	statuses: stringToArrayModel(publicCaseStatusSchema.array().nonempty()),
	from: z.iso.datetime({ offset: true }),
	until: z.iso.datetime({ offset: true })
});
const CASE_EXPORT_CSV_COLUMNS = [
	"created_at",
	"resolved_at",
	"workspace",
	"deployment",
	"case_name",
	"status",
	"type",
	"tags",
	"device",
	"linked_workorders",
	"description",
	"proposal",
	"solution",
	"assignee",
	"created_by"
];
const caseInsightsQueryParamsSchema = z.object({
	...caseFilterParamModel.shape,
	from: z.iso.datetime({ offset: true }),
	until: z.iso.datetime({ offset: true }),
	period: publicCasesInsightsPeriodSchema
});
const caseInsightsTimeInStatusQueryParamsSchema = caseInsightsQueryParamsSchema.omit({ period: true });
const caseInsightsPeriodSchema = z.strictObject({
	period_start: z.string(),
	period_end: z.string(),
	case_count: z.number(),
	case_ids: z.array(z.string())
});
const caseInsightsDurationsSchema = z.strictObject({
	avg_duration_ms: z.number().nullable(),
	p50_duration_ms: z.number().nullable(),
	p85_duration_ms: z.number().nullable(),
	p95_duration_ms: z.number().nullable()
});
const caseInsightsThroughputSchema = caseInsightsPeriodSchema;
const caseInsightsDurationSchema = caseInsightsPeriodSchema.extend(caseInsightsDurationsSchema.shape);
const caseInsightsTimeToFirstAssignmentSchema = caseInsightsDurationSchema.extend({ assigned_at_creation_count: z.number() });
const caseInsightsTimeInStatusSchema = z.strictObject({
	status: publicCaseStatusSchema,
	case_count: z.number(),
	open_case_count: z.number(),
	total_duration_ms: z.number().nullable(),
	case_ids: z.array(z.string()),
	...caseInsightsDurationsSchema.shape
});
/**
* Insert Disallowed: id, creator user, timestamps, serial ids (auto-generated), workspace_id (derived from the deployment),
* status (always reported), origin (set server-side), public portal contact fields (portal only)
* Insert Optional: assignee_id (default: deployment's default case assignee), case_tag_ids, maintenance_id (links the
* case as child of the maintenance), service_job_id (links the case to the service job) — in the same transaction
*/
const caseInsertSchema = z.strictObject({
	...casesInsertSchema.pick({
		deployment_id: true,
		name: true,
		reported_at: true,
		due_at: true,
		description: true,
		description_html: true,
		proposal: true,
		proposal_html: true,
		solution: true,
		solution_html: true,
		type: true,
		priority: true,
		event_started_at: true,
		event_finished_at: true,
		device_id: true,
		case_template_id: true,
		assignee_id: true
	}).shape,
	case_tag_ids: z.array(z.string()).optional(),
	maintenance_id: z.string().optional(),
	service_job_id: z.string().optional()
});
/**
* Update Allowed: content, status and its timestamps, type, priority, dates, assignee_id, device_id,
* deployment_id (workspace_id is re-derived; assignee and device must be valid in the target deployment)
* Update Disallowed: origin, public portal contact fields, case_template_id, serial ids
*/
const caseUpdateSchema = casesUpdateSchema.pick({
	name: true,
	description: true,
	description_html: true,
	proposal: true,
	proposal_html: true,
	solution: true,
	solution_html: true,
	status: true,
	type: true,
	priority: true,
	due_at: true,
	reported_at: true,
	assessed_at: true,
	started_at: true,
	resolved_at: true,
	event_started_at: true,
	event_finished_at: true,
	assignee_id: true,
	device_id: true,
	deployment_id: true
}).strict();
/**
* Insert Disallowed: workspace_id / deployment_id (derived from the case), case_id (route), creator user,
* edited_at / deleted_at (set server-side)
*/
const caseCommentInsertSchema = caseCommentsInsertSchema.pick({
	content: true,
	content_html: true,
	is_public: true,
	case_comment_parent_id: true
}).strict();
/**
* Update Allowed: content only (edited_at is set server-side)
*/
const caseCommentUpdateSchema = caseCommentsInsertSchema.pick({
	content: true,
	content_html: true
}).strict();
//#endregion
//#region src/contracts/internal/caseTags.ts
const caseTagSearchables = [{
	table: caseTagsTable,
	column: "name"
}, {
	table: caseTagsTable,
	column: "description"
}];
const caseTagSortables = [
	{
		table: caseTagsTable,
		column: "name"
	},
	{
		table: caseTagsTable,
		column: "updated_at"
	},
	{
		table: caseTagsTable,
		column: "created_at"
	}
];
const caseTagSchema = caseTagsRowSchema;
const caseTagListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: caseTagSortables,
		default_sort_by: "case_tags.name",
		default_sort_dir: "asc"
	}).shape
});
const caseTagCaseParamsSchema = idParamModel.extend({ case_id: idParamModel.shape.id });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated)
* Insert Required: workspace_id (global tags, workspace_id = null, are not creatable via API)
*/
const caseTagInsertSchema = caseTagsInsertSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true
}).extend({ workspace_id: z.string() });
/**
* Update Disallowed: id, creator user, timestamps (auto-generated), workspace_id (workspace change not allowed)
*/
const caseTagUpdateSchema = caseTagsUpdateSchema.omit({
	id: true,
	workspace_id: true,
	user_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/caseTemplates.ts
const caseTemplatesTable = "case_templates";
const caseTemplateSearchables = [
	{
		table: caseTemplatesTable,
		column: "name"
	},
	{
		table: caseTemplatesTable,
		column: "description"
	},
	{
		table: caseTemplatesTable,
		column: "proposal"
	},
	{
		table: caseTemplatesTable,
		column: "solution"
	}
];
const caseTemplateSortables = [
	{
		table: caseTemplatesTable,
		column: "name"
	},
	{
		table: caseTemplatesTable,
		column: "updated_at"
	},
	{
		table: caseTemplatesTable,
		column: "created_at"
	},
	{
		table: caseTemplatesTable,
		column: "type"
	}
];
const caseTemplateSchema = caseTemplatesRowSchema;
const caseTemplateExtendedSchema = z.strictObject({
	...caseTemplatesRowSchema.shape,
	public_portals: z.array(publicPortalsRowSchema),
	checklist_templates: z.array(checklistTemplatesRowSchema)
});
const caseTemplateListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: caseTemplateSortables,
		default_sort_by: "case_templates.name",
		default_sort_dir: "asc"
	}).shape,
	case_types: stringToArrayModel(publicCaseTypeSchema.array().nonempty().optional())
});
const caseTemplatePublicPortalParamsSchema = idParamModel.extend({ public_portal_id: idParamModel.shape.id });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated)
*/
const caseTemplateInsertSchema = caseTemplatesInsertSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true
});
/**
* Update Disallowed: id, creator user, timestamps (auto-generated), workspace_id (workspace change not allowed)
*/
const caseTemplateUpdateSchema = caseTemplatesUpdateSchema.omit({
	id: true,
	workspace_id: true,
	user_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/checklistTemplates.ts
const checklistTemplatesTable = "checklist_templates";
const checklistTemplateItemsTable = "checklist_template_items";
//#endregion
//#region src/contracts/internal/deploymentSettings.ts
const deploymentSettingsTable = "deployment_settings";
const deploymentSettingSearchables = [];
const deploymentSettingSortables = [];
const deploymentSettingSchema = deploymentSettingsRowSchema;
/**
* Update Disallowed: id, timestamps (auto-generated), deployment_id (deployment change not allowed)
*/
const deploymentSettingUpdateSchema = deploymentSettingsUpdateSchema.omit({
	id: true,
	deployment_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/deviceDataSeries.ts
const deviceDataSeriesTable = "device_data_series";
const deviceDataSeriesChartPointSchema = z.object({
	date: z.string(),
	value: z.number()
});
const deviceDataSeriesChartQueryParamsSchema = z.object({
	device_id: z.string(),
	target_table: publicDataTriggerTargetTableSchema,
	metric_name: z.string(),
	calculate_derivative: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean()),
	transform: publicDataTriggerTransformationSchema.optional(),
	time_window: z.string().optional(),
	from: z.iso.datetime({ offset: true }).optional(),
	until: z.iso.datetime({ offset: true }).optional()
}).refine((v) => !v.time_window || !!v.transform, {
	message: "a time window is only ever the window of a transform",
	path: ["time_window"]
}).refine((v) => v.target_table === "device_scalar_values" || !v.calculate_derivative && v.transform === "count", {
	message: "event tables always count",
	path: ["transform"]
});
//#endregion
//#region src/contracts/internal/deviceModelDataTriggers.ts
const deviceModelDataTriggersTable = "device_model_data_triggers";
const deviceModelDataTriggerSchema = deviceModelDataTriggersRowSchema;
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated)
*/
const deviceModelDataTriggerInsertSchema = deviceModelDataTriggersInsertSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/devices.ts
const devicesTable = "devices";
const deviceSchema = devicesRowSchema;
const deviceEventsTotalSchema = z.object({
	total: z.number(),
	last_data_at: z.string().nullable()
});
const deviceEventsTotalQueryParamsSchema = z.object({ target_table: publicDataTriggerTargetTableSchema.exclude(["device_scalar_values"]) });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), deletion, connection state
*/
const deviceInsertSchema = devicesInsertSchema.pick({
	workspace_id: true,
	deployment_id: true,
	name: true,
	ref: true,
	manufacturer_id: true,
	model_id: true,
	manufactured_at: true
});
/**
* Update Allowed: the device's own details - moving it to another deployment is a move (see deviceMoveSchema)
*/
const deviceUpdateSchema = devicesUpdateSchema.pick({
	name: true,
	ref: true,
	manufactured_at: true
});
const deviceMoveSchema = z.object({ deployment_id: z.string() });
//#endregion
//#region src/contracts/internal/maintenanceApprovalFiles.ts
const maintenanceApprovalFilesTable = "maintenance_approval_files";
const BUCKET_MAINTENANCE_APPROVAL_FILES = "maintenance-approval-files";
const maintenanceApprovalFileSortables = [{
	table: maintenanceApprovalFilesTable,
	column: "created_at"
}, {
	table: maintenanceApprovalFilesTable,
	column: "updated_at"
}];
const maintenanceApprovalFileSchema = maintenanceApprovalFilesRowSchema;
const maintenanceApprovalFileListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceApprovalFileSortables,
		default_sort_by: "maintenance_approval_files.created_at",
		default_sort_dir: "asc"
	}).shape,
	maintenance_approval_id: z.string().optional()
});
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), bucket_id (always BUCKET_MAINTENANCE_APPROVAL_FILES)
* Insert Required: maintenance_approval_id, path (of an already uploaded storage object)
*/
const maintenanceApprovalFileInsertSchema = maintenanceApprovalFilesInsertSchema.omit({
	id: true,
	user_id: true,
	bucket_id: true,
	created_at: true,
	updated_at: true
});
/**
* Update Allowed: file meta information only (title, description, thumbnail, locale)
*/
const maintenanceApprovalFileUpdateSchema = maintenanceApprovalFilesUpdateSchema.pick({
	title: true,
	description: true,
	thumbnail: true,
	locale: true
});
//#endregion
//#region src/contracts/internal/maintenanceApprovals.ts
const maintenanceApprovalsTable = "maintenance_approvals";
const maintenanceApprovalSortables = [{
	table: maintenanceApprovalsTable,
	column: "created_at"
}, {
	table: maintenanceApprovalsTable,
	column: "updated_at"
}];
const maintenanceApprovalSchema = maintenanceApprovalsRowSchema;
/**
* approver: the user profile of `approver_id` (null for external approvers, see approver_name/approver_email)
* executor: the user profile of `user_id` (the executor of the maintenance, not necessarily the creator)
*/
const maintenanceApprovalExtendedSchema = z.strictObject({
	...maintenanceApprovalsRowSchema.shape,
	approver: userProfilesRowSchema.nullable(),
	executor: userProfilesRowSchema.nullable()
});
const maintenanceApprovalListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceApprovalSortables,
		default_sort_by: "maintenance_approvals.created_at",
		default_sort_dir: "desc"
	}).shape,
	maintenance_id: z.string().optional()
});
/**
* Insert Disallowed: id, timestamps (auto-generated)
* Insert Required: maintenance_id, approver (either a user via approver_id, or an external approver via approver_email)
* Insert Optional: user_id is the executor of the maintenance (defaults to the creating user)
*/
const maintenanceApprovalInsertSchema = maintenanceApprovalsInsertSchema.omit({
	id: true,
	created_at: true,
	updated_at: true
}).refine((v) => Boolean(v.approver_id || v.approver_email), {
	message: "Either approver_id or approver_email is required",
	path: ["approver_id"]
});
/**
* Update Disallowed: id, timestamps (auto-generated), maintenance_id (maintenance change not allowed)
*/
const maintenanceApprovalUpdateSchema = maintenanceApprovalsUpdateSchema.omit({
	id: true,
	maintenance_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/maintenanceItemFiles.ts
const maintenanceItemFilesTable = "maintenance_item_files";
const BUCKET_MAINTENANCE_ITEM_FILES = "maintenance-item-files";
const maintenanceItemFileSchema = maintenanceItemFilesRowSchema;
const maintenanceItemFileListQueryParamsSchema = z.object({ maintenance_item_id: z.string() });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), bucket_id (always BUCKET_MAINTENANCE_ITEM_FILES)
* Insert Required: maintenance_item_id, path (of an already uploaded storage object, below the item's workspace folder)
*/
const maintenanceItemFileInsertSchema = maintenanceItemFilesInsertSchema.omit({
	id: true,
	user_id: true,
	bucket_id: true,
	created_at: true,
	updated_at: true
});
/**
* Update Allowed: file meta information only (title, description, thumbnail, locale)
*/
const maintenanceItemFileUpdateSchema = maintenanceItemFilesUpdateSchema.pick({
	title: true,
	description: true,
	thumbnail: true,
	locale: true
});
//#endregion
//#region src/contracts/internal/maintenanceItems.ts
const maintenanceItemTemplateFilesTable = "maintenance_item_template_files";
const MAINTENANCE_ITEM_STATUS_TRANSITIONS = {
	start: ["pending"],
	skip: ["pending", "started"],
	fail: ["pending", "started"],
	complete: ["pending", "started"],
	reset: [
		"skipped",
		"failed",
		"completed"
	]
};
const maintenanceItemBulkResolveStatusSchema = z.enum([
	"skipped",
	"failed",
	"completed"
]);
const UNIQUE_PARTS_PER_DEPLOYMENT_ERROR_CODE = "unique_parts_per_deployment";
const maintenanceItemSchema = maintenanceItemsRowSchema;
const maintenanceItemWithMaintenanceSchema = z.strictObject({
	...maintenanceItemsRowSchema.shape,
	maintenance: maintenancesRowSchema.pick({
		id: true,
		title: true,
		public_id: true,
		status: true
	}).nullable()
});
const maintenanceItemTemplateFileSchema = maintenanceItemTemplateFilesRowSchema;
const maintenanceItemDeviceListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	with_notes: z.preprocess((val) => typeof val === "string" ? val === "true" : val, z.boolean().optional()),
	statuses: stringToArrayModel(publicMaintenanceItemStatusSchema.array().nonempty().optional())
});
const maintenanceItemActionParamsSchema = idParamModel.extend({ action: z.enum(Object.keys(MAINTENANCE_ITEM_STATUS_TRANSITIONS)) });
/**
* Creates a custom item (task) of a maintenance's device, or a device log (an item without a maintenance)
* Insert Disallowed: workspace_id / deployment_id (derived from the maintenance, or the device), creator user, status
* (always pending), is_custom (always true), assignee, resolved_at
* Insert Required: device_id, name
* Insert Optional: maintenance_id (the device has to be linked to it), description, duration, ordering, started_at
* (eg. the prefilled start of a device log)
*/
const maintenanceItemInsertSchema = z.strictObject({
	...maintenanceItemsInsertSchema.pick({
		name: true,
		description: true,
		description_html: true,
		duration: true,
		ordering: true
	}).shape,
	started_at: z.iso.datetime({ offset: true }).nullable().optional(),
	device_id: z.string(),
	maintenance_id: z.string().optional()
});
/**
* Update Allowed: name, description, notes (worklog), planned duration
* Update Disallowed: status, assignee and dates (status actions), maintenance / device / deployment / workspace
*/
const maintenanceItemUpdateSchema = maintenanceItemsUpdateSchema.pick({
	name: true,
	description: true,
	description_html: true,
	notes: true,
	notes_html: true,
	duration: true
}).strict();
/**
* Status action of one item, see MAINTENANCE_ITEM_STATUS_TRANSITIONS — the actor becomes the assignee (reset clears
* it). The form's name / notes are saved with the action; `started_at` / `resolved_at` are custom dates (device
* logs), defaulting to: start → started now; skip → not started, resolved now; fail / complete → started as is,
* resolved now. Completing applies the item's part usage, skipping / failing removes it
*/
const maintenanceItemActionSchema = z.strictObject({
	...maintenanceItemsUpdateSchema.pick({
		name: true,
		notes: true,
		notes_html: true
	}).shape,
	started_at: z.iso.datetime({ offset: true }).nullable().optional(),
	resolved_at: z.iso.datetime({ offset: true }).optional()
});
/**
* Resolves all pending / started items of a maintenance (optionally of one device) at once, with the same effects
* as the single actions (completing applies part usage, skipping / failing removes it). `notes` replaces the notes of
* the resolved items (eg. a skip reason), their notes are kept without it
*/
const maintenanceItemsBulkResolveSchema = z.strictObject({
	maintenance_id: z.string(),
	device_id: z.string().optional(),
	status: maintenanceItemBulkResolveStatusSchema,
	notes: z.string().optional()
});
//#endregion
//#region src/contracts/internal/maintenanceTags.ts
const maintenanceTagsTable = "maintenance_tags";
const maintenanceTagSearchables = [{
	table: maintenanceTagsTable,
	column: "name"
}, {
	table: maintenanceTagsTable,
	column: "description"
}];
const maintenanceTagSortables = [
	{
		table: maintenanceTagsTable,
		column: "name"
	},
	{
		table: maintenanceTagsTable,
		column: "updated_at"
	},
	{
		table: maintenanceTagsTable,
		column: "created_at"
	}
];
const maintenanceTagSchema = maintenanceTagsRowSchema;
const maintenanceTagListQueryParamsSchema = z.object({
	...paginationParamModel.shape,
	...searchParamModel.shape,
	...getSortParamModel({
		sortables: maintenanceTagSortables,
		default_sort_by: "maintenance_tags.name",
		default_sort_dir: "asc"
	}).shape
});
const maintenanceTagMaintenanceStrategyParamsSchema = idParamModel.extend({ maintenance_strategy_id: idParamModel.shape.id });
const maintenanceTagMaintenanceParamsSchema = idParamModel.extend({ maintenance_id: idParamModel.shape.id });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated)
* Insert Required: workspace_id (global tags, workspace_id = null, are not creatable via API)
*/
const maintenanceTagInsertSchema = maintenanceTagsInsertSchema.omit({
	id: true,
	user_id: true,
	created_at: true,
	updated_at: true
}).extend({ workspace_id: z.string() });
/**
* Update Disallowed: id, creator user, timestamps (auto-generated), workspace_id (workspace change not allowed)
*/
const maintenanceTagUpdateSchema = maintenanceTagsUpdateSchema.omit({
	id: true,
	workspace_id: true,
	user_id: true,
	created_at: true,
	updated_at: true
});
//#endregion
//#region src/contracts/internal/portal.ts
const PortalTargetKind = {
	device: "device",
	deployment: "deployment"
};
const portalTargetKindSchema = z.enum(PortalTargetKind);
const PORTAL_DEVICES_DEFAULT_LIMIT = 5;
const PORTAL_DEVICES_MAX_LIMIT = 200;
const portalAccessBodySchema = z.object({
	portal_token: z.uuid(),
	portal_pin: z.string().nullish()
});
const portalDevicesBodySchema = z.object({
	...portalAccessBodySchema.shape,
	search: z.string().nullish(),
	limit: z.number().int().min(1).max(200).default(5)
});
/**
* device_id is only respected for deployment portals (verified to be a device of the portal deployment),
* device portals always attach the case to their own device
*/
const portalCaseInsertSchema = z.object({
	...portalAccessBodySchema.shape,
	name: z.string().trim().min(1),
	case_template_id: z.string().nullish(),
	description: z.string().nullish(),
	description_html: z.string().nullish(),
	proposal: z.string().nullish(),
	proposal_html: z.string().nullish(),
	solution: z.string().nullish(),
	solution_html: z.string().nullish(),
	type: publicCaseTypeSchema.nullish(),
	priority: z.number().int().min(1).max(5).nullish(),
	contact_name: z.string().nullish(),
	contact_phone: z.string().nullish(),
	contact_mail: z.string().nullish(),
	device_id: z.string().nullish()
});
const portalTargetSchema = z.object({
	target_kind: portalTargetKindSchema,
	portal_token: z.string(),
	device_id: z.string().nullable(),
	device_name: z.string().nullable(),
	device_manufacturer_name: z.string().nullable(),
	device_model_name: z.string().nullable(),
	deployment_name: z.string().nullable()
});
/**
* logo bucket/path are NOT a usable link (private bucket), they only tell the client that a logo exists,
* the signed url is minted by POST /portal/logo
*/
const portalDetailsSchema = z.object({
	portal_show_device_selector: z.boolean(),
	portal_show_workspace_profile_logo: z.boolean(),
	portal_workspace_profile_logo_bucket_id: z.string().nullable(),
	portal_workspace_profile_logo_path: z.string().nullable(),
	portal_intro_details: z.string().nullable(),
	portal_intro_details_html: z.string().nullable(),
	portal_instructions: z.string().nullable(),
	portal_instructions_html: z.string().nullable(),
	portal_contact_details: z.string().nullable(),
	portal_contact_details_html: z.string().nullable()
});
const portalDeviceSchema = z.object({
	device_id: z.string(),
	device_name: z.string(),
	device_manufacturer_name: z.string().nullable(),
	device_model_name: z.string().nullable()
});
const portalCaseTemplateSchema = caseTemplatesRowSchema.pick({
	id: true,
	type: true,
	priority: true,
	name: true,
	description: true,
	description_html: true,
	proposal: true,
	proposal_html: true,
	solution: true,
	solution_html: true
});
const portalCaseSchema = casesRowSchema.pick({
	id: true,
	public_id: true,
	name: true,
	status: true,
	reported_at: true,
	assessed_at: true,
	started_at: true,
	resolved_at: true,
	description: true,
	solution: true
});
const portalCaseCreatedSchema = z.object({ id: z.string() });
const portalLogoSchema = z.object({ url: z.string().nullable() });
//#endregion
//#region src/contracts/internal/publicPortals.ts
const publicPortalsTable = "public_portals";
const caseTemplatesToPublicPortalsTable = "case_templates_to_public_portals";
const pinSchema = z.string().regex(/^\d{4}$/).nullish();
const publicPortalSchema = publicPortalsRowSchema;
/**
* deployment: the deployment of the portal
* case_templates: the case templates linked to the portal, which are visible to the user
*/
const publicPortalExtendedSchema = z.strictObject({
	...publicPortalsRowSchema.shape,
	deployment: deploymentsRowSchema,
	case_templates: z.array(caseTemplatesRowSchema)
});
const caseTemplateToPublicPortalSchema = caseTemplatesToPublicPortalsRowSchema;
const publicPortalListQueryParamsSchema = z.object({ deployment_id: z.string().optional() });
const publicPortalCaseTemplateParamsSchema = idParamModel.extend({ case_template_id: idParamModel.shape.id });
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), workspace_id (derived from the deployment)
*/
const publicPortalInsertSchema = publicPortalsInsertSchema.omit({
	id: true,
	user_id: true,
	workspace_id: true,
	created_at: true,
	updated_at: true
}).extend({ pin: pinSchema });
/**
* Update Disallowed: id, creator user, timestamps (auto-generated), workspace_id & deployment_id (change not allowed)
*/
const publicPortalUpdateSchema = publicPortalsUpdateSchema.omit({
	id: true,
	user_id: true,
	workspace_id: true,
	deployment_id: true,
	created_at: true,
	updated_at: true
}).extend({ pin: pinSchema });
//#endregion
//#region src/contracts/internal/qrCodes.ts
const qrCodesTable = "qr_codes";
const exactlyOneToken = (v) => Boolean(v.device_portal_token) !== Boolean(v.deployment_portal_token);
const exactlyOneTokenMessage = {
	message: "Either device_portal_token or deployment_portal_token is required",
	path: ["device_portal_token"]
};
const qrCodeSchema = qrCodesRowSchema;
const qrCodeActiveQueryParamsSchema = z.object({
	device_portal_token: z.uuid().optional(),
	deployment_portal_token: z.uuid().optional()
}).refine(exactlyOneToken, exactlyOneTokenMessage);
/**
* Insert Disallowed: id, creator user, timestamps (auto-generated), scan data, revoked_at (a new qr_code is active),
* workspace_id (derived from the deployment)
* Insert Required: deployment_id, either device_portal_token or deployment_portal_token (of the deployment)
*/
const qrCodeInsertSchema = qrCodesInsertSchema.omit({
	id: true,
	user_id: true,
	workspace_id: true,
	created_at: true,
	updated_at: true,
	scan_count: true,
	scanned_at: true,
	revoked_at: true
}).extend({
	device_portal_token: z.uuid().nullish(),
	deployment_portal_token: z.uuid().nullish()
}).refine(exactlyOneToken, exactlyOneTokenMessage);
/**
* Update Allowed: revoked_at (revoking), settings
*/
const qrCodeUpdateSchema = qrCodesUpdateSchema.pick({
	revoked_at: true,
	settings: true
});
//#endregion
//#region src/contracts/internal/workspaceInvitations.ts
const workspaceInvitationsTable = "workspace_invitations";
const workspaceInvitationSchema = workspaceInvitationsRowSchema;
const workspaceInvitationCreateSchema = z.object({
	email: z.string().trim().toLowerCase().pipe(z.email()),
	locale: z.string().optional(),
	workspace_id: z.string().min(1),
	workspace_role: publicWorkspaceRoleSchema
});
//#endregion
export { BUCKET_CASE_FILES, BUCKET_MAINTENANCE_APPROVAL_FILES, BUCKET_MAINTENANCE_ITEM_FILES, CASE_CLOSED_STATUSES, CASE_EDITABLE_STATUSES, CASE_EXPORT_CSV_COLUMNS, MAINTENANCE_ASSIGNABLE_DEPLOYMENT_ROLES, MAINTENANCE_EXPORT_CSV_COLUMNS, MAINTENANCE_ITEM_EXPORT_CSV_COLUMNS, MAINTENANCE_ITEM_STATUS_TRANSITIONS, MAINTENANCE_SKIP_REASON_AUTO_SKIP, MAINTENANCE_STATUS_TRANSITIONS, MAINTENANCE_STRATEGY_NO_SCHEDULE_INTERVAL, PORTAL_DEVICES_DEFAULT_LIMIT, PORTAL_DEVICES_MAX_LIMIT, PortalTargetKind, STAGING_PART_LOCATION_TEMPLATE_NAME, UNIQUE_PARTS_PER_DEPLOYMENT_ERROR_CODE, VIRTUAL_MAINTENANCE_PREFIX, caseActivityQueryParamsSchema, caseActivitySchema, caseByIdsQueryParamsSchema, caseCaseTagParamsSchema, caseCommentInsertSchema, caseCommentParamsSchema, caseCommentSchema, caseCommentUpdateSchema, caseCommentsTable, caseCustomFilterSchema, caseDeviceDataTriggerSchema, caseExportQueryParamsSchema, caseExtendedSchema, caseFileInsertSchema, caseFileListQueryParamsSchema, caseFileSchema, caseFileUpdateSchema, caseFilesTable, caseInsertSchema, caseInsightsDurationSchema, caseInsightsQueryParamsSchema, caseInsightsThroughputSchema, caseInsightsTimeInStatusQueryParamsSchema, caseInsightsTimeInStatusSchema, caseInsightsTimeToFirstAssignmentSchema, caseLinkedMaintenanceSchema, caseListQueryParamsSchema, caseLogListQueryParamsSchema, caseLogSchema, caseMaintenanceLinkSchema, caseMaintenanceParamsSchema, caseSchema, caseSearchables, caseServiceJobLinkSchema, caseServiceJobParamsSchema, caseSortables, caseTagCaseParamsSchema, caseTagInsertSchema, caseTagLinkSchema, caseTagListQueryParamsSchema, caseTagSchema, caseTagSearchables, caseTagSortables, caseTagUpdateSchema, caseTagsTable, caseTemplateExtendedSchema, caseTemplateInsertSchema, caseTemplateListQueryParamsSchema, caseTemplatePublicPortalParamsSchema, caseTemplateSchema, caseTemplateSearchables, caseTemplateSortables, caseTemplateToPublicPortalSchema, caseTemplateUpdateSchema, caseTemplatesTable, caseTemplatesToPublicPortalsTable, caseUpdateSchema, casesHistoryTable, casesTable, casesToCaseTagsHistoryTable, casesToCaseTagsTable, casesToDeviceDataTriggersTable, casesToMaintenancesHistoryTable, casesToMaintenancesTable, casesToPartsHistoryTable, casesToPartsTable, casesToServiceJobsHistoryTable, casesToServiceJobsTable, checklistTemplateItemsTable, checklistTemplatesTable, deploymentSettingSchema, deploymentSettingSearchables, deploymentSettingSortables, deploymentSettingUpdateSchema, deploymentSettingsTable, deviceDataSeriesChartPointSchema, deviceDataSeriesChartQueryParamsSchema, deviceDataSeriesTable, deviceDataTriggerCurrentValueSchema, deviceDataTriggerEvaluationSchema, deviceDataTriggerInsertSchema, deviceDataTriggerSchema, deviceDataTriggerUpdateSchema, deviceDataTriggerWithSeriesSchema, deviceDataTriggersTable, deviceEventsTotalQueryParamsSchema, deviceEventsTotalSchema, deviceInsertSchema, deviceModelDataTriggerInsertSchema, deviceModelDataTriggerSchema, deviceModelDataTriggersTable, deviceMoveSchema, deviceSchema, deviceUpdateSchema, devicesTable, entityTreeNodeSchema, maintenanceActivityQueryParamsSchema, maintenanceActivitySchema, maintenanceApprovalExtendedSchema, maintenanceApprovalFileInsertSchema, maintenanceApprovalFileListQueryParamsSchema, maintenanceApprovalFileSchema, maintenanceApprovalFileSortables, maintenanceApprovalFileUpdateSchema, maintenanceApprovalFilesTable, maintenanceApprovalInsertSchema, maintenanceApprovalListQueryParamsSchema, maintenanceApprovalSchema, maintenanceApprovalSortables, maintenanceApprovalUpdateSchema, maintenanceApprovalsHistoryTable, maintenanceApprovalsTable, maintenanceByIdsQueryParamsSchema, maintenanceCaseParamsSchema, maintenanceChildParamsSchema, maintenanceCountSchema, maintenanceCustomFilterSchema, maintenanceDeviceParamsSchema, maintenanceDeviceReorderSchema, maintenanceDeviceSchema, maintenanceDeviceUpdateSchema, maintenanceDevicesDeleteQueryParamsSchema, maintenanceDevicesInsertSchema, maintenanceExportQueryParamsSchema, maintenanceExtendedSchema, maintenanceFinishSchema, maintenanceFollowupDurationSchema, maintenanceFromStrategyCreateSchema, maintenanceInsertSchema, maintenanceInsightsAvgResolutionQueryParamsSchema, maintenanceInsightsAvgResolutionSchema, maintenanceInsightsByStatusTagPeriodSchema, maintenanceInsightsDeviceUtilizationSchema, maintenanceInsightsFinishedCountSchema, maintenanceInsightsPeriodQueryParamsSchema, maintenanceInsightsQueryParamsSchema, maintenanceInsightsResolveTimeByDeviceSchema, maintenanceInsightsResolveTimeSchema, maintenanceItemActionParamsSchema, maintenanceItemActionSchema, maintenanceItemBulkResolveStatusSchema, maintenanceItemDeviceListQueryParamsSchema, maintenanceItemExportQueryParamsSchema, maintenanceItemFileInsertSchema, maintenanceItemFileListQueryParamsSchema, maintenanceItemFileSchema, maintenanceItemFileUpdateSchema, maintenanceItemFilesTable, maintenanceItemInsertSchema, maintenanceItemSchema, maintenanceItemTemplateFileSchema, maintenanceItemTemplateFilesTable, maintenanceItemUpdateSchema, maintenanceItemWithMaintenanceSchema, maintenanceItemsBulkResolveSchema, maintenanceItemsTable, maintenanceItemsToPartsTable, maintenanceLinkedCaseSchema, maintenanceLinkedMaintenanceSchema, maintenanceListQueryParamsSchema, maintenanceLogListQueryParamsSchema, maintenanceLogSchema, maintenanceMaintainedDevicesCountQueryParamsSchema, maintenanceMaintenanceLinkSchema, maintenanceMaintenanceTagLinkSchema, maintenanceMaintenanceTagParamsSchema, maintenanceParentParamsSchema, maintenanceSchema, maintenanceSearchables, maintenanceSkipSchema, maintenanceSortables, maintenanceStrategiesHistoryTable, maintenanceStrategiesTable, maintenanceStrategiesToDevicesTable, maintenanceStrategiesToMaintenanceTagsTable, maintenanceStrategyActiveSchema, maintenanceStrategyDeviceReorderSchema, maintenanceStrategyDeviceSchema, maintenanceStrategyDevicesDeleteQueryParamsSchema, maintenanceStrategyDevicesInsertSchema, maintenanceStrategyDevicesUpdateSchema, maintenanceStrategyExcludedDateInsertSchema, maintenanceStrategyExtendedSchema, maintenanceStrategyInsertSchema, maintenanceStrategyListQueryParamsSchema, maintenanceStrategyLogListQueryParamsSchema, maintenanceStrategyLogSchema, maintenanceStrategyLogSortables, maintenanceStrategyMaintenanceTagParamsSchema, maintenanceStrategyMaintenanceTagSchema, maintenanceStrategySchema, maintenanceStrategySearchables, maintenanceStrategySetActiveSchema, maintenanceStrategySortables, maintenanceStrategySummarySchema, maintenanceStrategyUpdateSchema, maintenanceTagInsertSchema, maintenanceTagListQueryParamsSchema, maintenanceTagMaintenanceParamsSchema, maintenanceTagMaintenanceStrategyParamsSchema, maintenanceTagSchema, maintenanceTagSearchables, maintenanceTagSortables, maintenanceTagUpdateSchema, maintenanceTagsTable, maintenanceUpcomingVirtualQueryParamsSchema, maintenanceUpdateSchema, maintenanceViewSchema, maintenancesHistoryTable, maintenancesTable, maintenancesToDeviceDataTriggersTable, maintenancesToDevicesHistoryTable, maintenancesToDevicesTable, maintenancesToMaintenanceTagsHistoryTable, maintenancesToMaintenanceTagsTable, maintenancesToMaintenancesHistoryTable, maintenancesToMaintenancesTable, maintenancesToServiceJobsHistoryTable, maintenancesToServiceJobsTable, portalAccessBodySchema, portalCaseCreatedSchema, portalCaseInsertSchema, portalCaseSchema, portalCaseTemplateSchema, portalDetailsSchema, portalDeviceSchema, portalDevicesBodySchema, portalLogoSchema, portalTargetKindSchema, portalTargetSchema, publicPortalCaseTemplateParamsSchema, publicPortalExtendedSchema, publicPortalInsertSchema, publicPortalListQueryParamsSchema, publicPortalSchema, publicPortalUpdateSchema, publicPortalsTable, qrCodeActiveQueryParamsSchema, qrCodeInsertSchema, qrCodeSchema, qrCodeUpdateSchema, qrCodesTable, registerUserBodySchema, resetPasswordBodySchema, serviceJobCaseParamsSchema, workspaceInvitationCreateSchema, workspaceInvitationSchema, workspaceInvitationsTable };
